/*
 * This file is part of SketchBand.
 * Copyright (C) 2016  Mikael Brockman
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

import React from "react"
import { createRoot } from "react-dom/client"
import PouchDB from "pouchdb-browser"

import { Manager } from "./manager.jsx"

if ("serviceWorker" in navigator) {
  navigator.serviceWorker.getRegistrations().then(registrations => {
    registrations.forEach(registration => registration.unregister())
  })
}

if ("caches" in window) {
  caches.keys().then(keys => {
    keys
      .filter(key => key.startsWith("sketchband-"))
      .forEach(key => caches.delete(key))
  })
}

const db = new PouchDB("sketch.band")

let appState = window.state = {
  hash: location.hash,
  songs: [],
  transpositions: {}
}

// Custom pseudo-Redux
function applyEvent(state, event) {
  switch (event.type) {
    case "app-loaded":
      return state
    case "hash-changed":
      return { ...state, hash: event.hash }
    case "songs-loaded":
      return { ...state, songs: event.songs }
    case "song-changed":
      return { ...state,
        songs: state.songs.map(
          x => x._id === event.song._id ? event.song : x
        )
      }
    case "song-added":
      return { ...state,
        songs: [...state.songs, event.song]
      }
    case "transposition-changed":
      return {
        ...state,
        transpositions: {
          ...state.transpositions,
          [event.songId]: event.semitones
        }
      }
    default:
      throw new Error(`Unhandled event type ${event.type}`)
  }
}

function App(props) {
  return (
    <Manager
      db={db}
      hash={props.hash}
      songs={props.songs.filter(x => !x._deleted)}
      transpositions={props.transpositions}
    />
  )
}

const div = document.getElementById("app")
const root = createRoot(div)

function setAppState(state) {
  appState = window.state = state
  root.render(<App {...state} />)
}

function dispatch(type, payload = {}, timestamp = new Date) {
  console.log(timestamp, type, payload)
  setAppState(applyEvent(appState, { type, timestamp, ...payload }))
}

// XXX: hacky, replace with some fancy Redux crap
window.dispatch = dispatch

window.onhashchange = () =>
  dispatch("hash-changed", { hash: location.hash })

dispatch("app-loaded")

db.allDocs({ include_docs: true }).then(function(result) {
  const songs = result.rows.map(x => x.doc)
  dispatch("songs-loaded", { songs })

  if (localStorage.getItem("sync-url")) {
    PouchDB.sync("sketch.band", localStorage.getItem("sync-url"), { live: true })
  }

  db.changes({
    live: true,
    include_docs: true,
  }).on("change", function(change) {
    if (appState.songs.filter(x => x._id === change.id).length > 0) {
      dispatch("song-changed", { song: change.doc })
    } else {
      dispatch("song-added", { song: change.doc })
    }
  })
})

window.db = db
